import {
  formatDate,
  formatNumber,
  routes,
  TIME_GROUPS,
  TIME_MODES,
  TIME_STATUS_FILTERS,
  timeBucketLabel,
  type TimeGroup,
  type TimeMode,
  type TimeOverview,
  timePeriodSchema,
  todayIso,
} from '@pmai/contracts';
import {
  Alert,
  Button,
  ChoiceButtons,
  EmptyState,
  PageHeader,
  SelectField,
  Sheet,
  SheetBody,
  SheetHead,
  SheetRow,
  SheetTd,
  SheetTh,
  Spinner,
  TextField,
} from '@pmai/ui';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useApiQuery } from '../../shared/api/hooks';

type Status = (typeof TIME_STATUS_FILTERS)[number];
type Row = TimeOverview['rows'][number];

/** This calendar month (FR-OVR-008). */
function thisMonth() {
  const today = todayIso();
  const [year, month] = today.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return { from: `${today.slice(0, 8)}01`, to: `${today.slice(0, 8)}${String(last).padStart(2, '0')}` };
}

/** Hours with up to 2 decimals; an empty cell is "·" (FR-OVR-012). */
const hours = (h: number | undefined) => (h ? formatNumber(h) : '·');

/** Laika pārskats (FR-OVR-008…012): worklog hours by person or project and period. */
export function TimeOverviewPage() {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(thisMonth);
  const [period, setPeriod] = useState(thisMonth);
  const [error, setError] = useState<string | null>(null);
  const [group, setGroup] = useState<TimeGroup>('day');
  const [status, setStatus] = useState<Status>('active');
  const [mode, setMode] = useState<TimeMode>('person');
  const report = useApiQuery(
    routes.timeOverview.get,
    { query: { ...period, group, status, mode } },
    { placeholderData: (previous) => previous },
  );
  const show = () => {
    const checked = timePeriodSchema.safeParse(draft);
    setError(checked.success ? null : (checked.error.issues[0]?.message ?? null));
    if (checked.success) setPeriod(draft);
  };

  return (
    <>
      <PageHeader title={t('timeOverview.title')} />
      <div className="flex flex-col gap-4">
        <form
          noValidate
          className="flex flex-wrap items-end gap-4 rounded-card border border-border bg-surface p-4"
          onSubmit={(e) => {
            e.preventDefault();
            show();
          }}
        >
          <TextField
            label={t('timeOverview.from')}
            type="date"
            value={draft.from}
            onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
          />
          <TextField
            label={t('timeOverview.to')}
            type="date"
            value={draft.to}
            onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
          />
          <Button type="submit" icon="search">
            {t('timeOverview.show')}
          </Button>
          <div className="w-44">
            <SelectField
              label={t('timeOverview.group')}
              value={group}
              onChange={(e) => setGroup(e.target.value as TimeGroup)}
              options={TIME_GROUPS.map((g) => ({ value: g, label: t(`timeOverview.groups.${g}`) }))}
            />
          </div>
          <div className="w-64">
            <SelectField
              label={t('timeOverview.status')}
              value={status}
              onChange={(e) => setStatus(e.target.value as Status)}
              options={TIME_STATUS_FILTERS.map((s) => ({ value: s, label: t(`timeOverview.statuses.${s}`) }))}
            />
          </div>
          <ChoiceButtons
            showLabel
            label={t('timeOverview.mode')}
            value={mode}
            onChange={setMode}
            options={TIME_MODES.map((m) => ({ value: m, label: t(`timeOverview.modes.${m}`) }))}
          />
        </form>
        {error && <Alert tone="danger">{error}</Alert>}
        {report.data === undefined ? (
          <Spinner label={t('common.loading')} />
        ) : (
          <>
            <p className="text-sm text-ink-muted" data-testid="time-overview-meta">
              {metaLine(t, period, mode, report.data)}
            </p>
            {report.data.rows.length === 0 ? (
              <EmptyState icon="clock" title={t('timeOverview.empty')} />
            ) : (
              <TimeTable report={report.data} group={group} mode={mode} />
            )}
          </>
        )}
      </div>
    </>
  );
}

function metaLine(
  t: TFunction,
  period: { from: string; to: string },
  mode: TimeMode,
  r: TimeOverview,
): string {
  const range = t('timeOverview.period', { from: formatDate(period.from), to: formatDate(period.to) });
  if (r.rows.length === 0) return `${range} · ${t('timeOverview.noData')}`;
  return [
    range,
    t(`timeOverview.primaryCount.${mode}`, { n: r.rows.length }),
    t('timeOverview.subCount', { n: r.rows.reduce((a, row) => a + row.rows.length, 0) }),
    t('timeOverview.worklogCount', { n: r.worklogs }),
    t('timeOverview.totalHours', { hours: formatNumber(r.total) }),
  ].join(' · ');
}

/** Primary rows (highlighted, with totals) and their sub-rows; columns per period (FR-OVR-009/010). */
function TimeTable({ report, group, mode }: { report: TimeOverview; group: TimeGroup; mode: TimeMode }) {
  const { t } = useTranslation();
  const other: TimeMode = mode === 'person' ? 'project' : 'person';
  const name = (row: Row | Row['rows'][number]) => {
    if (row.projectId)
      return (
        <Link to={`/projects/${row.projectId}`} className="text-brand-700 hover:underline">
          {row.name}
        </Link>
      );
    return row.mapped ? row.name : t('timeOverview.unmapped', { name: row.name }).trim();
  };
  const cells = (row: { cells: Record<string, number>; total: number }) => [
    ...report.buckets.map((b) => (
      <SheetTd key={b} data-col={b} tone={row.cells[b] ? 'plain' : 'computed'}>
        {hours(row.cells[b])}
      </SheetTd>
    )),
    <SheetTd key="total" data-col="total" tone="total">
      {formatNumber(row.total)}
    </SheetTd>,
  ];

  return (
    <Sheet label={t('timeOverview.title')}>
      <SheetHead>
        <SheetRow>
          <SheetTh sticky>{t(`timeOverview.modes.${mode}`)}</SheetTh>
          <SheetTh>{t(`timeOverview.modes.${other}`)}</SheetTh>
          {report.buckets.map((b) => (
            <SheetTh key={b}>{timeBucketLabel(b, group)}</SheetTh>
          ))}
          <SheetTh tone="total">{t('common.total')}</SheetTh>
        </SheetRow>
      </SheetHead>
      <SheetBody>
        {report.rows.map((row) => [
          <SheetRow key={row.key} kind="total" rowKey={row.key}>
            <SheetTd sticky align="left">
              {name(row)}
            </SheetTd>
            <SheetTd />
            {cells(row)}
          </SheetRow>,
          ...row.rows.map((sub) => (
            <SheetRow key={`${row.key}/${sub.key}`} rowKey={`${row.key}/${sub.key}`}>
              <SheetTd sticky />
              <SheetTd align="left">{name(sub)}</SheetTd>
              {cells(sub)}
            </SheetRow>
          )),
        ])}
        <SheetRow kind="total" rowKey="total">
          <SheetTd sticky align="left">
            {t('timeOverview.grandTotal')}
          </SheetTd>
          <SheetTd />
          {cells({ cells: report.columnTotals, total: report.total })}
        </SheetRow>
      </SheetBody>
    </Sheet>
  );
}
